import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useGetTelephonySettingsQuery, useUpdateCallSummariesMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

export default function CallSummariesCard() {
  const { data: settings, isLoading } = useGetTelephonySettingsQuery();
  const [updateCallSummaries, { isLoading: isSaving }] = useUpdateCallSummariesMutation();
  const [enabled, setEnabled] = useState(false);

  async function handleToggle(next: boolean) {
    setEnabled(next);
    try {
      await updateCallSummaries({ enabled: next }).unwrap();
      toast.success("Setting updated");
    } catch (error: unknown) {
      setEnabled(!next);
      toast.error(apiErrorMessage(error, "Failed to update setting"));
    }
  }

  useEffect(() => {
    if (settings) {
      setEnabled(settings.callSummariesEnabled);
    }
  }, [settings]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Call transcripts and summaries</CardTitle>
        <CardDescription>Know what was said on every recorded power dialer call without listening back.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-row items-start justify-between rounded-lg border p-4">
          <div className="space-y-1 pr-4">
            <p className="text-base font-medium">Transcribe and summarize dialer calls</p>
            <p className="text-sm text-muted-foreground">
              Lets power dialer campaigns write out and summarize their recorded conversations with key points, a
              next step and a suggested follow-up task. Each campaign turns it on in its own settings. It is charged
              at <span className="font-medium tabular-nums">${settings?.transcriptionRate ?? 0}</span> per
              transcribed minute. Calls are only transcribed when recording is on. With this off, nothing is
              transcribed or charged.
            </p>
          </div>
          <Switch checked={enabled} disabled={isLoading || isSaving} onCheckedChange={handleToggle} />
        </div>
      </CardContent>
    </Card>
  );
}
